import React, { useState } from 'react';
import { faFlag } from '@fortawesome/free-solid-svg-icons';
import { MeasurementTypeEnum, MeasurementSignificanceEnum } from '../enums/measurementTypeEnum';
import { CustomDropdown } from '@medprompt/shared';
import './AddMeasurementForm.css';

const MeasurementComparator = {
  EQ: { value: 'EQ', display: '=', symbol: '' },
  GT: { value: 'GT', display: '>', symbol: '>' },
  LT: { value: 'LT', display: '<', symbol: '<' }
};

const getDefaultUnitForType = (type) => MeasurementTypeEnum[type]?.defaultUnit ?? '';

const AddMeasurementForm = ({ doc, onConfirm, onCancel }) => {
  const measurementTypes = Object.keys(MeasurementTypeEnum);

  const initialType = measurementTypes[0] ?? '';
  const [selectedType, setSelectedType] = useState(initialType);
  const [selectedComparator, setSelectedComparator] = useState('EQ');
  const [value, setValue] = useState('');
  const [unit, setUnit] = useState(() => getDefaultUnitForType(initialType));
  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false);
  const [isComparatorDropdownOpen, setIsComparatorDropdownOpen] = useState(false);

  const isValid = selectedType && value && unit && selectedComparator;

  const handleConfirm = () => {
    if (!isValid) return;

    const parsedValue = parseFloat(value);
    if (isNaN(parsedValue)) return;

    onConfirm({
      type: selectedType,
      comparator: selectedComparator,
      value: parsedValue,
      unit: unit.trim()
    });
  };

  const typeOptions = measurementTypes
    .map(type => {
      const meta = MeasurementTypeEnum[type];
      return {
        value: type,
        display: meta?.displayName || type,
        description: meta?.description,
        ...(meta?.significance === MeasurementSignificanceEnum.CORE.value && { icon: faFlag }),
      };
    })
    .sort((a, b) => a.display.localeCompare(b.display));

  const selectedIcon =
    MeasurementTypeEnum[selectedType]?.significance === MeasurementSignificanceEnum.CORE.value
      ? faFlag
      : undefined;

  return (
    <div className="document-ai-item measurement-item add-measurement-segment">
      <div className="add-measurement-form">
        <div className="form-group">
          <label className="form-label">Measurement Type</label>
          <CustomDropdown
            searchable
            searchPlaceholder="Search measurement types..."
            selected={MeasurementTypeEnum[selectedType]?.displayName || selectedType}
            selectedIcon={selectedIcon}
            options={typeOptions}
            isOpen={isTypeDropdownOpen}
            toggleDropdown={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
            onSelect={(option) => {
              setSelectedType(option.value);
              setUnit(getDefaultUnitForType(option.value));
              setIsTypeDropdownOpen(false);
            }}
            keyExtractor={(option, index) => `measurement-type-${option.value || index}`}
            className="measurement-type-dropdown"
            maxHeight="500px"
          />
          {selectedType && MeasurementTypeEnum[selectedType]?.description && (
            <div className="field-description">
              {MeasurementTypeEnum[selectedType].description}
            </div>
          )}
        </div>

        <div className="form-group">
          <label className="form-label">Value</label>
          <div className="value-input-group">
            <CustomDropdown
              selected={MeasurementComparator[selectedComparator]?.display || '='}
              options={Object.values(MeasurementComparator)}
              isOpen={isComparatorDropdownOpen}
              toggleDropdown={() => setIsComparatorDropdownOpen(!isComparatorDropdownOpen)}
              onSelect={(option) => {
                setSelectedComparator(option.value);
                setIsComparatorDropdownOpen(false);
              }}
              className="measurement-comparator-dropdown-form"
              width="70px"
            />
            <input
              type="number"
              step="any"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              className="measurement-value-input-form"
              placeholder="Enter value"
            />
          </div>
        </div>

        <div className="form-group">
          <label className="form-label">Unit</label>
          <input
            type="text"
            value={unit}
            onChange={(e) => setUnit(e.target.value)}
            className="measurement-unit-input-form"
            placeholder="e.g., mmHg, kg, cm"
          />
        </div>
      </div>

      <div className="add-measurement-form-actions">
        <button type="button" className="add-measurement-form-cancel-button" onClick={onCancel}>
          Cancel
        </button>
        <button
          type="button"
          className="header-button header-button-primary"
          onClick={handleConfirm}
          disabled={!isValid}
        >
          Add Measurement
        </button>
      </div>
    </div>
  );
};

export default React.memo(AddMeasurementForm);
